import {
  createDefaultBackgroundSound,
  normalizeBackgroundSound,
} from './backgroundSound'

/** 默认「预设静默提示语」条数 */
export const DEFAULT_SILENCE_PROMPT_COUNT = 1

export const HMC_TRIGGER_MODE = {
  LLM: 'llm',
  KEYWORD: 'keyword',
}

export const HMC_ON_FAILURE = {
  HANGUP: 'hangup',
  LLM_CONTINUE: 'llm_continue',
}

/** 打断设置 · 基础模式 */
export const INTERRUPT_BASE_MODE = {
  ALLOW: 'allow',
  DENY: 'deny',
}

/** 转接内容填入示例 */
export const HMC_TRANSFER_EXAMPLE =
  '稍等，正在为您转接人工坐席，请稍后！'

export const HMC_LLM_PROMPT_PLACEHOLDER =
  '编写帮助：\n1. 请根据业务需求，丰富「转接场景」与「示例流程」的描述。\n2. 请在「不应转接场景」下列出需要排除的特殊情况。\n\n默认描述'

/** 插入记忆变量选项 */
export const MEMORY_INSERT_OPTIONS = [
  { token: '{{mem:lastone}}', label: '前一通' },
  { token: '{{mem:total}}', label: '全部' },
]

export function createDefaultHmcConfig() {
  return {
    trigger_mode: HMC_TRIGGER_MODE.LLM,
    llm_prompt: '',
    keywords: [],
    transfer: {
      text: '',
      audio_file_path: '',
    },
    on_failure: HMC_ON_FAILURE.LLM_CONTINUE,
  }
}

export function createDefaultInterruptSettings() {
  return {
    enabled: false,
    base_mode: INTERRUPT_BASE_MODE.ALLOW,
    filler_words: [],
    utterance_lock_enabled: false,
    lock_words: [],
    force_interrupt_enabled: false,
    force_interrupt_words: [],
    deny_force_interrupt_enabled: false,
    deny_force_interrupt_words: [],
  }
}

export function createEmptySilencePrompt(text = '') {
  return {
    text: String(text || ''),
    audio_file_path: '',
  }
}

export function createDefaultCallControl() {
  return {
    memory_enabled: false,
    is_hmc: false,
    hmc_config: createDefaultHmcConfig(),
    interrupt_settings: createDefaultInterruptSettings(),
    max_call_duration: 180,
    call_timeout: 35,
    preset_opening_enabled: false,
    transition_sound_enabled: false,
    preset_opening: {
      text: '',
      audio_file_path: '',
    },
    input_timeout: 8000,
    silence_prompts: [createEmptySilencePrompt('喂，您还在吗？')],
    max_timeouts: 3,
    goodbye: {
      text: '好的，明白了，那我先挂了，再见！',
      audio_file_path: '',
    },
    background_sound: createDefaultBackgroundSound(),
  }
}

/** 插入停顿时长 → 【停顿X秒】 */
export function formatPauseTag(seconds) {
  const n = Number(seconds)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n === 0.5) return '【停顿0.5秒】'
  if (n === 1) return '【停顿1秒】'
  if (n === 2) return '【停顿2秒】'
  const rounded = Number.isInteger(n) ? String(n) : String(n)
  return `【停顿${rounded}秒】`
}

export function getAudioFileName(path) {
  const s = String(path != null ? path : '').trim()
  if (!s) return ''
  const parts = s.split(/[/\\]/)
  return parts[parts.length - 1] || s
}

function isSilencePromptRowEmpty(row) {
  if (!row) return true
  const text = row.text != null ? String(row.text).trim() : ''
  const audio =
    row.audio_file_path != null
      ? String(row.audio_file_path).trim()
      : row.audioFilePath != null
        ? String(row.audioFilePath).trim()
        : ''
  return !text && !audio
}

function normalizeSilencePrompts(raw) {
  if (Array.isArray(raw) && raw.length) {
    const mapped = raw.map((row) => ({
      text: row && row.text != null ? String(row.text) : '',
      audio_file_path:
        row && row.audio_file_path != null
          ? String(row.audio_file_path)
          : row && row.audioFilePath != null
            ? String(row.audioFilePath)
            : '',
    }))
    while (
      mapped.length > DEFAULT_SILENCE_PROMPT_COUNT &&
      isSilencePromptRowEmpty(mapped[mapped.length - 1])
    ) {
      mapped.pop()
    }
    return mapped.length ? mapped : createDefaultCallControl().silence_prompts
  }
  return createDefaultCallControl().silence_prompts
}

function normalizeGoodbye(raw) {
  if (raw && typeof raw === 'object') {
    return {
      text:
        raw.text != null
          ? String(raw.text)
          : createDefaultCallControl().goodbye.text,
      audio_file_path:
        raw.audio_file_path != null
          ? String(raw.audio_file_path)
          : raw.audioFilePath != null
            ? String(raw.audioFilePath)
            : '',
    }
  }
  return createDefaultCallControl().goodbye
}

function normalizePresetOpening(raw) {
  if (raw && typeof raw === 'object') {
    return {
      text: raw.text != null ? String(raw.text) : '',
      audio_file_path:
        raw.audio_file_path != null
          ? String(raw.audio_file_path)
          : raw.audioFilePath != null
            ? String(raw.audioFilePath)
            : '',
    }
  }
  if (typeof raw === 'string') {
    return { text: raw, audio_file_path: '' }
  }
  return { text: '', audio_file_path: '' }
}

function normalizeKeywordList(raw) {
  if (Array.isArray(raw)) {
    return raw.map((k) => String(k != null ? k : '').trim()).filter(Boolean)
  }
  const s = raw != null ? String(raw).trim() : ''
  if (!s) return []
  try {
    return s
      .split(/(?<![\\]);/)
      .map((item) => item.replace(/\\;/g, ';').trim())
      .filter(Boolean)
  } catch (e) {
    return s
      .split(';')
      .map((item) => item.trim())
      .filter(Boolean)
  }
}

function normalizeHmcConfig(raw) {
  const defaults = createDefaultHmcConfig()
  const src = raw && typeof raw === 'object' ? raw : {}
  const mode = String(src.trigger_mode || src.triggerMode || '').trim()
  const triggerMode =
    mode === HMC_TRIGGER_MODE.KEYWORD ? HMC_TRIGGER_MODE.KEYWORD : HMC_TRIGGER_MODE.LLM
  const transferRaw = src.transfer && typeof src.transfer === 'object' ? src.transfer : {}
  const failure = String(src.on_failure || src.onFailure || '').trim()
  const onFailure =
    failure === HMC_ON_FAILURE.HANGUP ? HMC_ON_FAILURE.HANGUP : HMC_ON_FAILURE.LLM_CONTINUE
  return {
    trigger_mode: triggerMode,
    llm_prompt:
      src.llm_prompt != null
        ? String(src.llm_prompt)
        : src.llmPrompt != null
          ? String(src.llmPrompt)
          : defaults.llm_prompt,
    keywords: normalizeKeywordList(src.keywords != null ? src.keywords : src.keyword_list),
    transfer: {
      text: transferRaw.text != null ? String(transferRaw.text) : '',
      audio_file_path:
        transferRaw.audio_file_path != null
          ? String(transferRaw.audio_file_path)
          : transferRaw.audioFilePath != null
            ? String(transferRaw.audioFilePath)
            : '',
    },
    on_failure: onFailure,
  }
}

export function normalizeInterruptSettings(raw) {
  const src = raw && typeof raw === 'object' ? raw : {}
  const mode = String(src.base_mode != null ? src.base_mode : src.baseMode || '').trim()
  return {
    enabled: src.enabled === true,
    base_mode: mode === INTERRUPT_BASE_MODE.DENY ? INTERRUPT_BASE_MODE.DENY : INTERRUPT_BASE_MODE.ALLOW,
    filler_words: normalizeKeywordList(src.filler_words != null ? src.filler_words : src.fillerWords),
    utterance_lock_enabled: src.utterance_lock_enabled === true || src.utteranceLockEnabled === true,
    lock_words: normalizeKeywordList(src.lock_words != null ? src.lock_words : src.lockWords),
    force_interrupt_enabled: src.force_interrupt_enabled === true || src.forceInterruptEnabled === true,
    force_interrupt_words: normalizeKeywordList(
      src.force_interrupt_words != null ? src.force_interrupt_words : src.forceInterruptWords,
    ),
    deny_force_interrupt_enabled:
      src.deny_force_interrupt_enabled === true || src.denyForceInterruptEnabled === true,
    deny_force_interrupt_words: normalizeKeywordList(
      src.deny_force_interrupt_words != null
        ? src.deny_force_interrupt_words
        : src.denyForceInterruptWords,
    ),
  }
}

/** 保证 callControl 上存在完整的 interrupt_settings */
export function ensureInterruptSettings(callControl) {
  if (!callControl || typeof callControl !== 'object') return
  const normalized = normalizeInterruptSettings(callControl.interrupt_settings)
  if (!callControl.interrupt_settings || typeof callControl.interrupt_settings !== 'object') {
    callControl.interrupt_settings = { ...normalized }
    return
  }
  Object.keys(normalized).forEach((key) => {
    if (
      key === 'filler_words' ||
      key === 'lock_words' ||
      key === 'force_interrupt_words' ||
      key === 'deny_force_interrupt_words'
    ) {
      callControl.interrupt_settings[key] = [...normalized[key]]
      return
    }
    callControl.interrupt_settings[key] = normalized[key]
  })
}

/** 保证 callControl 上存在完整的 hmc_config（表单绑定用） */
export function ensureHmcConfig(callControl) {
  if (!callControl || typeof callControl !== 'object') return
  if (!callControl.hmc_config || typeof callControl.hmc_config !== 'object') {
    callControl.hmc_config = createDefaultHmcConfig()
    return
  }
  const normalized = normalizeHmcConfig(callControl.hmc_config)
  Object.keys(normalized).forEach((key) => {
    if (key === 'transfer') {
      if (!callControl.hmc_config.transfer || typeof callControl.hmc_config.transfer !== 'object') {
        callControl.hmc_config.transfer = { ...normalized.transfer }
      } else {
        callControl.hmc_config.transfer.text = normalized.transfer.text
        callControl.hmc_config.transfer.audio_file_path = normalized.transfer.audio_file_path
      }
      return
    }
    if (key === 'keywords') {
      callControl.hmc_config.keywords = [...normalized.keywords]
      return
    }
    callControl.hmc_config[key] = normalized[key]
  })
}

/** 从 llmsettings.call_control_settings.call_control 恢复到表单 */
export function applyCallControlToForm(source, target, options = {}) {
  if (!target || typeof target !== 'object') return
  const isCustomPreset = options.isCustomPreset === true
  const defaults = createDefaultCallControl()
  const cc = source && typeof source === 'object' ? source : {}

  target.memory_enabled = cc.memory_enabled === true

  target.is_hmc = cc.is_hmc === true

  target.hmc_config = normalizeHmcConfig(cc.hmc_config)

  target.interrupt_settings = normalizeInterruptSettings(cc.interrupt_settings)

  if (cc.max_call_duration != null && Number.isFinite(Number(cc.max_call_duration))) {
    target.max_call_duration = Math.max(1, Math.floor(Number(cc.max_call_duration)))
  } else {
    target.max_call_duration = defaults.max_call_duration
  }

  if (cc.call_timeout != null && Number.isFinite(Number(cc.call_timeout))) {
    target.call_timeout = Math.max(1, Math.floor(Number(cc.call_timeout)))
  } else {
    target.call_timeout = defaults.call_timeout
  }

  target.preset_opening_enabled = cc.preset_opening_enabled === true

  if (isCustomPreset && cc.transition_sound_enabled == null) {
    target.transition_sound_enabled = true
  } else {
    target.transition_sound_enabled = cc.transition_sound_enabled === true
  }

  target.preset_opening = normalizePresetOpening(cc.preset_opening)

  if (cc.input_timeout != null && Number.isFinite(Number(cc.input_timeout))) {
    target.input_timeout = Math.max(1, Math.floor(Number(cc.input_timeout)))
  } else {
    target.input_timeout = defaults.input_timeout
  }

  target.silence_prompts = normalizeSilencePrompts(cc.silence_prompts)

  if (cc.max_timeouts != null && Number.isFinite(Number(cc.max_timeouts))) {
    target.max_timeouts = Math.max(1, Math.floor(Number(cc.max_timeouts)))
  } else {
    target.max_timeouts = defaults.max_timeouts
  }

  target.goodbye = normalizeGoodbye(cc.goodbye)

  target.background_sound = normalizeBackgroundSound(cc.background_sound)
}

/** 写入 llmsettings.call_control_settings.call_control */
export function buildCallControlSnapshot(callControl, options = {}) {
  const isCustomPreset = options.isCustomPreset === true
  const cc = callControl && typeof callControl === 'object' ? callControl : createDefaultCallControl()
  const maxDuration = (() => {
    const x = Number(cc.max_call_duration)
    if (!Number.isFinite(x)) return 180
    return Math.max(1, Math.floor(x))
  })()
  const callTimeout = (() => {
    const x = Number(cc.call_timeout)
    if (!Number.isFinite(x)) return 35
    return Math.max(1, Math.floor(x))
  })()
  const inputTimeout = (() => {
    const x = Number(cc.input_timeout)
    if (!Number.isFinite(x)) return 8000
    return Math.max(1, Math.floor(x))
  })()
  const maxTimeouts = (() => {
    const x = Number(cc.max_timeouts)
    if (!Number.isFinite(x)) return 3
    return Math.max(1, Math.floor(x))
  })()
  const silencePrompts = (Array.isArray(cc.silence_prompts) ? cc.silence_prompts : []).map((row) => ({
    text: String(row && row.text != null ? row.text : '').trim(),
    audio_file_path: String(
      row && row.audio_file_path != null ? row.audio_file_path : '',
    ).trim(),
  }))
  const presetOpening = cc.preset_opening && typeof cc.preset_opening === 'object' ? cc.preset_opening : {}
  const goodbye = cc.goodbye && typeof cc.goodbye === 'object' ? cc.goodbye : {}
  const backgroundSound = normalizeBackgroundSound(cc.background_sound)
  const hmcConfig = normalizeHmcConfig(cc.hmc_config)
  const interruptSettings = normalizeInterruptSettings(cc.interrupt_settings)

  return {
    memory_enabled: cc.memory_enabled === true,
    is_hmc: cc.is_hmc === true,
    hmc_config: {
      trigger_mode: hmcConfig.trigger_mode,
      llm_prompt: String(hmcConfig.llm_prompt || '').trim(),
      keywords: normalizeKeywordList(hmcConfig.keywords),
      transfer: {
        text: String(hmcConfig.transfer.text || '').trim(),
        audio_file_path: String(hmcConfig.transfer.audio_file_path || '').trim(),
      },
      on_failure: hmcConfig.on_failure,
    },
    interrupt_settings: {
      enabled: interruptSettings.enabled === true,
      base_mode: interruptSettings.base_mode,
      filler_words: normalizeKeywordList(interruptSettings.filler_words),
      utterance_lock_enabled: interruptSettings.utterance_lock_enabled === true,
      lock_words: normalizeKeywordList(interruptSettings.lock_words),
      force_interrupt_enabled: interruptSettings.force_interrupt_enabled === true,
      force_interrupt_words: normalizeKeywordList(interruptSettings.force_interrupt_words),
      deny_force_interrupt_enabled: interruptSettings.deny_force_interrupt_enabled === true,
      deny_force_interrupt_words: normalizeKeywordList(interruptSettings.deny_force_interrupt_words),
    },
    max_call_duration: maxDuration,
    call_timeout: callTimeout,
    preset_opening_enabled: cc.preset_opening_enabled === true,
    transition_sound_enabled: isCustomPreset && cc.transition_sound_enabled === true,
    preset_opening: {
      text: String(presetOpening.text != null ? presetOpening.text : '').trim(),
      audio_file_path: String(
        presetOpening.audio_file_path != null ? presetOpening.audio_file_path : '',
      ).trim(),
    },
    input_timeout: inputTimeout,
    silence_prompts: silencePrompts,
    max_timeouts: maxTimeouts,
    goodbye: {
      text: String(goodbye.text != null ? goodbye.text : '').trim(),
      audio_file_path: String(
        goodbye.audio_file_path != null ? goodbye.audio_file_path : '',
      ).trim(),
    },
    background_sound: backgroundSound.sound_id
      ? {
          sound_id: backgroundSound.sound_id,
          sound_name: backgroundSound.sound_name,
          audio_file_path: backgroundSound.audio_file_path,
          volume: backgroundSound.volume,
        }
      : {
          sound_id: '',
          sound_name: '',
          audio_file_path: '',
          volume: backgroundSound.volume,
        },
  }
}
